import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { NAV } from "@/data/content";
import { cn } from "@/lib/utils";

const linkBase =
  "relative whitespace-nowrap text-sm font-medium text-navy/80 transition-colors hover:text-navy";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openDrop, setOpenDrop] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDrop(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled
          ? "border-b border-line/70 bg-white/95 backdrop-blur-xl shadow-[0_2px_20px_rgba(6,16,43,0.06)]"
          : "border-b border-line/60 bg-white/90 backdrop-blur-xl"
      )}
    >
      <div className="mx-auto flex h-[74px] max-w-shell items-center justify-between px-6 md:h-[88px] md:px-12">
        <Link to="/" data-testid="nav-logo" aria-label="Innervation Energy Group home">
          <img
            src="/ieg-lockup-light.png?v=4"
            alt="Innervation Energy Group"
            className="h-11 w-auto md:h-12 xl:h-14"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 xl:flex xl:gap-8" data-testid="desktop-nav">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.path}
                className="relative"
                onMouseEnter={() => setOpenDrop(true)}
                onMouseLeave={() => setOpenDrop(false)}
              >
                <NavLink
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    cn(linkBase, "flex items-center gap-1", isActive && "text-navy")
                  }
                >
                  {item.label}
                  <ChevronDown
                    className={cn(
                      "h-3.5 w-3.5 transition-transform",
                      openDrop && "rotate-180"
                    )}
                  />
                </NavLink>
                <AnimatePresence>
                  {openDrop && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 top-full w-72 pt-4"
                    >
                      <div className="overflow-hidden rounded-sm border border-line bg-white shadow-lg">
                        {item.children.map((c) => (
                          <Link
                            key={c.path}
                            to={c.path}
                            data-testid={`nav-sub-${c.path.split("/").pop()}`}
                            className="group flex items-center justify-between border-b border-line px-5 py-4 text-sm text-navy/80 transition-colors last:border-b-0 hover:bg-ice hover:text-navy"
                          >
                            {c.label}
                            <ArrowUpRight className="h-4 w-4 text-slatey opacity-0 transition-opacity group-hover:opacity-100" />
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn(linkBase, "link-underline", isActive && "text-navy")
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="nav-login"
            className="hidden whitespace-nowrap text-sm font-medium text-navy/70 transition-colors hover:text-navy sm:inline-flex"
          >
            Log in
          </Link>
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            className="hidden items-center gap-2 whitespace-nowrap bg-electric px-6 py-3 text-sm font-medium text-white transition-transform duration-200 hover:scale-[0.97] hover:bg-electric-hover sm:inline-flex"
          >
            Contact Us
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="inline-flex h-11 w-11 items-center justify-center border border-line text-navy xl:hidden"
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-line bg-white xl:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col px-6 py-4">
              {NAV.map((item) =>
                item.children ? (
                  <div key={item.path} className="border-b border-line">
                    <button
                      className="flex w-full items-center justify-between py-4 text-left text-base font-medium text-navy"
                      onClick={() => setMobileSub((v) => !v)}
                    >
                      {item.label}
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 transition-transform",
                          mobileSub && "rotate-180"
                        )}
                      />
                    </button>
                    <AnimatePresence>
                      {mobileSub && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: "auto" }}
                          exit={{ height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="flex flex-col pb-3 pl-4">
                            <Link
                              to={item.path}
                              className="py-2 text-sm text-slatey hover:text-electric"
                            >
                              All Solutions
                            </Link>
                            {item.children.map((c) => (
                              <Link
                                key={c.path}
                                to={c.path}
                                className="py-2 text-sm text-slatey hover:text-electric"
                              >
                                {c.label}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="border-b border-line py-4 text-base font-medium text-navy"
                  >
                    {item.label}
                  </Link>
                )
              )}
              <Link
                to="/login"
                data-testid="mobile-login"
                className="mt-5 inline-flex items-center justify-center gap-2 border border-line px-6 py-4 text-sm font-medium text-navy"
              >
                Log in to Portal
              </Link>
              <Link
                to="/contact"
                data-testid="mobile-contact-cta"
                className="mt-3 inline-flex items-center justify-center gap-2 bg-electric px-6 py-4 text-sm font-medium text-white"
              >
                Contact Us
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
